// Returns HTTP status code from diverse error shapes (Axios-like or custom)
export const getHttpStatus = (error: unknown): number | undefined => {
  if (typeof error !== 'object' || error === null) {
    return undefined;
  }

  const maybe = error as { status?: number; response?: { status?: number } };
  return maybe.status ?? maybe.response?.status;
};

/**
 * Extracts a user-friendly error message from various error formats.
 * Handles Axios errors, Error objects, strings, and other error shapes.
 *
 * @param error - The error to extract a message from
 * @param defaultMessage - Default message if no message can be extracted
 * @returns A user-friendly error message
 */
export const getErrorMessage = (
  error: unknown,
  defaultMessage = 'An error occurred',
): string => {
  if (typeof error === 'string') {
    return error;
  }

  if (error instanceof Error) {
    return error.message || defaultMessage;
  }

  if (typeof error === 'object' && error !== null) {
    const err = error as {
      response?: { data?: { message?: string } };
      data?: { message?: string };
      message?: string;
    };

    // Axios error with response.data.message
    if (err.response?.data?.message) {
      return err.response.data.message;
    }

    // Error with data.message (when response is passed directly)
    if (err.data?.message) {
      return err.data.message;
    }

    // Error with message property
    if (err.message) {
      return err.message;
    }
  }

  return defaultMessage;
};

// Returns error code from diverse error shapes (Axios-like or custom)
export const getErrorCode = (error: unknown): string | undefined => {
  if (typeof error !== 'object' || error === null) {
    return undefined;
  }

  const errorObj = error as {
    error?: string;
    data?: { error?: string };
    response?: { data?: { error?: string } };
  };

  return (
    errorObj.error ?? errorObj.data?.error ?? errorObj.response?.data?.error
  );
};
